"use client";
import { useState, useEffect } from "react";
import { useTranslations } from "next-intl";
import { Link } from "@/i18n/navigation";

type CookieConsent = {
  necessary: true;
  functional: boolean;
  analytics: boolean;
};

const CONSENT_KEY = "salaria_cookie_consent";

function getOrCreateSessionId(): string {
  if (typeof window === "undefined") return "";
  const KEY = "salaria_session_id";
  let sid = localStorage.getItem(KEY);
  if (!sid) {
    sid = crypto.randomUUID();
    localStorage.setItem(KEY, sid);
  }
  return sid;
}


export function useCookieConsent() {
  const [consent, setConsent] = useState<CookieConsent | null>(null);
  const t = useTranslations("cookies");

  useEffect(() => {
    const stored = localStorage.getItem(CONSENT_KEY);
    if (stored) {
      try {
        setConsent(JSON.parse(stored));
      } catch {
        setConsent(null);
      }
    }
  }, []);

  const saveConsent = (c: CookieConsent, source: 'banner' | 'settings' | 'withdraw' = 'banner') => {
    localStorage.setItem(CONSENT_KEY, JSON.stringify(c));
    setConsent(c);

    // PostHog
   /** if (c.analytics && typeof window !== "undefined" && (window as any).posthog) {
      (window as any).posthog.opt_in_capturing();
    } else if (!c.analytics && typeof window !== "undefined" && (window as any).posthog) {
      (window as any).posthog.opt_out_capturing();
    }
    */

    // Cookiebot
    if (typeof window !== "undefined" && (window as any).Cookiebot) {
      try {
        (window as any).Cookiebot.submitCustomConsent(
          c.functional,
          c.analytics,
          false
        );
      } catch {}
    }

    // Логирование на backend (fire-and-forget, не блокируем UI)
    if (typeof window !== "undefined") {
      const sessionId = getOrCreateSessionId();
      fetch('/api/consent/log', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sessionId,
          domain: window.location.hostname,
          consent: {
            necessary: c.necessary,
            functional: c.functional,
            analytics: c.analytics,
          },
          source,
        }),
      }).catch(() => {
        // Не критично если backend временно недоступен — у нас есть localStorage
      });
    }
  };


  return { consent, saveConsent };
}

export default function CookieBanner() {
  const { consent, saveConsent } = useCookieConsent();
  const [showDetails, setShowDetails] = useState(false);
  const [functional, setFunctional] = useState(true);
  const [analytics, setAnalytics] = useState(true);
  const [visible, setVisible] = useState(false);
  const t = useTranslations("cookies");

  useEffect(() => {
    if (!consent) {
      // Небольшая задержка чтобы не мелькало при SSR
      const t = setTimeout(() => setVisible(true), 800);
      return () => clearTimeout(t);
    }
  }, [consent]);

  if (!visible || consent) return null;

  const acceptAll = () => {
    saveConsent({ necessary: true, functional: true, analytics: true });
    setVisible(false);
  };

  const acceptNecessary = () => {
    saveConsent({ necessary: true, functional: false, analytics: false });
    setVisible(false);
  };

  const saveCustom = () => {
    saveConsent({ necessary: true, functional, analytics });
    setVisible(false);
  };

  return (
   <div className="fixed bottom-0 start-0 end-0 z-[999] p-4 sm:p-6 pb-[calc(1rem+env(safe-area-inset-bottom))] sm:pb-[calc(1.5rem+env(safe-area-inset-bottom))]">
      <div className="max-w-2xl mx-auto bg-white border border-zinc-200 rounded-2xl shadow-xl overflow-hidden">
        <div className="p-5 sm:p-6">
          {/* Header */}
          <div className="flex items-start gap-3 mb-4">
            <div className="w-8 h-8 rounded-full bg-zinc-900 flex items-center justify-center flex-shrink-0 mt-0.5">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"/><path d="M12 16v-4M12 8h.01"/>
              </svg>
            </div>
            <div>
              <h3 className="text-sm font-semibold text-zinc-900">{t("title")}</h3>
              <p className="text-xs text-zinc-500 mt-0.5 leading-relaxed">
                {t("description")}{" "}
                <Link href="/privacy" className="underline underline-offset-2 hover:text-zinc-900 transition-colors">
                  {t("privacyLink")}
                </Link>
              </p>
            </div>
          </div>

          {/* Details toggle */}
          {showDetails && (
            <div className="mb-4 space-y-2 border-t border-zinc-100 pt-4">
              {/* Necessary */}
              <div className="flex items-center justify-between py-2">
                <div>
                  <p className="text-xs font-medium text-zinc-900">{t("necessary")}</p>
                  <p className="text-xs text-zinc-400">{t("necessaryDesc")}</p>
                </div>
                <div className="w-8 h-4 bg-zinc-900 rounded-full flex items-center justify-end px-0.5">
                  <div className="w-3 h-3 bg-white rounded-full" />
                </div>
              </div>

              {/* Functional */}
              <div className="flex items-center justify-between py-2">
                <div>
                  <p className="text-xs font-medium text-zinc-900">{t("functional")}</p>
                  <p className="text-xs text-zinc-400">{t("functionalDesc")}</p>
                </div>
                <button
                  onClick={() => setFunctional(!functional)}
                  className={`w-8 h-4 rounded-full flex items-center px-0.5 transition-colors ${functional ? "bg-zinc-900 justify-end" : "bg-zinc-200 justify-start"}`}
                >
                  <div className="w-3 h-3 bg-white rounded-full" />
                </button>
              </div>

              {/* Analytics */}
              <div className="flex items-center justify-between py-2">
                <div>
                  <p className="text-xs font-medium text-zinc-900">{t("analytics")}</p>
                  <p className="text-xs text-zinc-400">{t("analyticsDesc")}</p>
                </div>
                <button
                  onClick={() => setAnalytics(!analytics)}
                  className={`w-8 h-4 rounded-full flex items-center px-0.5 transition-colors ${analytics ? "bg-zinc-900 justify-end" : "bg-zinc-200 justify-start"}`}
                >
                  <div className="w-3 h-3 bg-white rounded-full" />
                </button>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={acceptAll}
              className="px-4 py-2 bg-zinc-900 text-white text-xs font-medium rounded-lg hover:bg-zinc-700 transition-colors"
            >
              {t("acceptAll")}
            </button>
            <button
              onClick={acceptNecessary}
              className="px-4 py-2 bg-zinc-100 text-zinc-700 text-xs font-medium rounded-lg hover:bg-zinc-200 transition-colors"
            >
              {t("necessaryOnly")}
            </button>
            {showDetails ? (
              <button
                onClick={saveCustom}
                className="px-4 py-2 bg-zinc-100 text-zinc-700 text-xs font-medium rounded-lg hover:bg-zinc-200 transition-colors"
              >
                {t("saveChoice")}
              </button>
            ) : (
              <button
                onClick={() => setShowDetails(true)}
                className="px-4 py-2 text-zinc-400 text-xs font-medium rounded-lg hover:text-zinc-700 transition-colors"
              >
                {t("customize")}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}